import type { GameDefinition, Outcome, Rng, Seat, Viewer } from '@campfire/engine';
import { ACE_HIGH, deal, shuffle, standardDeck, type Card } from '@campfire/cards';

export interface WarOptions {
  /** Face-down cards each player commits in a war before the deciding flip. Classic: 1 or 3. */
  readonly faceDownPerWar: number;
  /** End after this many resolved battles; more cards wins. null = play to completion. */
  readonly roundCap: number | null;
  /** Shuffle won cards before adding to the pile; guarantees termination with probability 1. */
  readonly shuffleWinnings: boolean;
}

type Pair<T> = readonly [T, T];

export interface WarState {
  /** Index 0 = top of pile. Hidden from every viewer, including the owner. */
  readonly piles: Pair<readonly Card[]>;
  /** Cards flipped in the current battle; the last element is the live card. */
  readonly faceUp: Pair<readonly Card[]>;
  readonly faceDown: Pair<readonly Card[]>;
  readonly flipped: Pair<boolean>;
  readonly phase: 'flip' | 'resolve' | 'done';
  readonly rounds: number;
  readonly wars: number;
  readonly result: Outcome | null;
  readonly options: WarOptions;
  /** Face-up cards of the most recently decided battle, kept visible until the next one (FR-WAR-08). */
  readonly lastBattle: LastBattle | null;
}

export interface LastBattle {
  readonly faceUp: Pair<readonly Card[]>;
  readonly winner: Seat;
  /** Total cards collected, including face-down war cards. */
  readonly taken: number;
}

export type WarAction = { readonly type: 'flip' } | { readonly type: 'resolve' };

export interface WarView {
  readonly you: Seat | null;
  readonly pileCounts: Pair<number>;
  readonly faceUp: Pair<readonly Card[]>;
  readonly faceDownCounts: Pair<number>;
  readonly flipped: Pair<boolean>;
  readonly phase: WarState['phase'];
  readonly rounds: number;
  readonly wars: number;
  readonly result: Outcome | null;
  readonly lastBattle: LastBattle | null;
}

const set = <T>(pair: Pair<T>, i: Seat, v: T): Pair<T> => (i === 0 ? [v, pair[1]] : [pair[0], v]);
const other = (s: Seat): Seat => (s === 0 ? 1 : 0);
const at = <T>(pair: Pair<T>, i: Seat): T => (i === 0 ? pair[0] : pair[1]);
const EMPTY: Pair<readonly Card[]> = [[], []];

function win(state: WarState, winner: Seat, reason: string): WarState {
  return { ...state, phase: 'done', result: { winners: [winner], draw: false, reason } };
}

function byCount(state: WarState, reason: string): WarState {
  const [a, b] = state.piles.map((p) => p.length) as [number, number];
  if (a === b) return { ...state, phase: 'done', result: { winners: [], draw: true, reason } };
  return win(state, a > b ? 0 : 1, reason);
}

function resolve(state: WarState, rng: Rng): WarState {
  const top0 = state.faceUp[0].at(-1) as Card;
  const top1 = state.faceUp[1].at(-1) as Card;
  const cmp = ACE_HIGH.compare(top0, top1);
  const pot = [...state.faceUp[0], ...state.faceDown[0], ...state.faceUp[1], ...state.faceDown[1]];

  if (cmp !== 0) {
    const w: Seat = cmp > 0 ? 0 : 1;
    const winnings = state.options.shuffleWinnings ? shuffle(pot, rng) : pot;
    const next: WarState = {
      ...state,
      lastBattle: { faceUp: state.faceUp, winner: w, taken: pot.length },
      piles: set(state.piles, w, [...at(state.piles, w), ...winnings]),
      faceUp: EMPTY,
      faceDown: EMPTY,
      flipped: [false, false],
      phase: 'flip',
      rounds: state.rounds + 1,
    };
    if (at(next.piles, other(w)).length === 0) return win(next, w, 'all-cards');
    const cap = state.options.roundCap;
    if (cap !== null && next.rounds >= cap) return byCount(next, 'round-cap');
    return next;
  }

  // Tie → war. Each player needs n face-down cards plus one to flip.
  const n = state.options.faceDownPerWar;
  const can0 = state.piles[0].length >= n + 1;
  const can1 = state.piles[1].length >= n + 1;
  const wars = state.wars + 1;
  if (can0 && can1) {
    return {
      ...state,
      piles: [state.piles[0].slice(n), state.piles[1].slice(n)],
      faceDown: [
        [...state.faceDown[0], ...state.piles[0].slice(0, n)],
        [...state.faceDown[1], ...state.piles[1].slice(0, n)],
      ],
      flipped: [false, false],
      phase: 'flip',
      wars,
    };
  }
  if (!can0 && !can1) return { ...state, wars, phase: 'done', result: { winners: [], draw: true, reason: 'both-cannot-war' } };
  const w: Seat = can0 ? 0 : 1;
  const l = other(w);
  const piles = set(set(state.piles, w, [...at(state.piles, w), ...pot, ...at(state.piles, l)]), l, []);
  const lastBattle: LastBattle = { faceUp: state.faceUp, winner: w, taken: pot.length };
  return win({ ...state, wars, piles, faceUp: EMPTY, faceDown: EMPTY, lastBattle }, w, 'opponent-cannot-war');
}

type WarGame = GameDefinition<WarState, WarAction, WarView, WarOptions>;

export const war: WarGame = {
  id: 'war',
  name: 'War',
  rules: [
    'Two players, each with half the deck, face down. Win all the cards. This is a game of chance.',
    'Each player taps Flip to turn their top card at the same time. The higher card wins both; aces are high and suits do not matter.',
    'The winner of a battle takes all the cards. The app shuffles the won cards before putting them under the winner’s pile, so a game always ends eventually.',
    'If the cards tie, it is a war: each player puts cards face down and flips another card, and the highest of that takes the whole pile.',
    'Setting: Cards face down in a war (1 card or 3 cards). A player who doesn’t have enough cards for a war loses; if neither does, it is a draw.',
    'Setting: Round limit. No limit, or 50, 100, 250 or 500 battles; at the limit the player with more cards wins, and equal counts are a draw.',
    'You win when your opponent has no cards. The app enforces everything; there are no decisions to make.',
  ],
  minSeats: 2,
  maxSeats: 2,
  defaultOptions: { faceDownPerWar: 1, roundCap: null, shuffleWinnings: true },
  optionSpecs: [
    {
      key: 'faceDownPerWar',
      label: 'Cards face down in a war',
      choices: [
        { value: 1, label: '1 card' },
        { value: 3, label: '3 cards' },
      ],
    },
    {
      key: 'roundCap',
      label: 'Round limit',
      help: 'At the limit, whoever holds more cards wins.',
      choices: [
        { value: null, label: 'No limit' },
        ...[50, 100, 250, 500].map((n) => ({ value: n, label: `${n} rounds` })),
      ],
    },
  ],

  setup({ options, rng }) {
    if (!Number.isInteger(options.faceDownPerWar) || options.faceDownPerWar < 0) {
      throw new RangeError('faceDownPerWar must be a non-negative integer');
    }
    if (options.roundCap !== null && (!Number.isInteger(options.roundCap) || options.roundCap < 1)) {
      throw new RangeError('roundCap must be a positive integer or null');
    }
    const { hands } = deal(shuffle(standardDeck(), rng), 2, { perHand: 'all' });
    return {
      piles: [hands[0] as Card[], hands[1] as Card[]],
      faceUp: EMPTY,
      faceDown: EMPTY,
      flipped: [false, false],
      phase: 'flip',
      rounds: 0,
      wars: 0,
      result: null,
      options,
      lastBattle: null,
    };
  },

  activeSeats: (s) => (s.phase === 'flip' ? ([0, 1] as const).filter((i) => !s.flipped[i]) : []),

  validate(s, action, seat) {
    if (action.type !== 'flip') return { ok: false, reason: 'only the engine may resolve battles' };
    if (s.phase !== 'flip') return { ok: false, reason: 'not the flip phase' };
    if (s.flipped[seat]) return { ok: false, reason: 'already flipped' };
    return { ok: true };
  },

  apply(s, action, { actor, rng }) {
    if (action.type === 'resolve') return resolve(s, rng);
    const seat = actor as Seat;
    const pile = at(s.piles, seat);
    const flipped = set(s.flipped, seat, true);
    return {
      ...s,
      piles: set(s.piles, seat, pile.slice(1)),
      faceUp: set(s.faceUp, seat, [...at(s.faceUp, seat), pile[0] as Card]),
      flipped,
      phase: flipped[0] && flipped[1] ? 'resolve' : 'flip',
    };
  },

  autoAction: (s) => (s.phase === 'resolve' ? { type: 'resolve' } : null),

  view: (s, viewer: Viewer) => ({
    you: viewer.kind === 'seat' ? viewer.seat : null,
    pileCounts: [s.piles[0].length, s.piles[1].length],
    faceUp: s.faceUp,
    faceDownCounts: [s.faceDown[0].length, s.faceDown[1].length],
    flipped: s.flipped,
    phase: s.phase,
    rounds: s.rounds,
    wars: s.wars,
    result: s.result,
    lastBattle: s.lastBattle,
  }),

  outcome: (s) => s.result,

  enumerateActions: (s, seat) => (s.phase === 'flip' && !s.flipped[seat] ? [{ type: 'flip' }] : []),
};
